import { generatePath } from 'react-router';

import { MODULES } from 'constants/ModuleConstants';
import { PATH } from 'constants/RoutesConstants';
import type { ModuleEnum } from 'modules/global/typing/enums/module.enum';
import { navigate } from 'modules/router/navigate';

type QueryParams = Record<string, string | number | boolean | null | undefined>;

type GeneratePathParams = Record<string, string | null | undefined>;

type OriginParam = { origin?: string };

const navigateTo = (route: string, params: QueryParams = {}, redirect = false, state?: unknown): void => {
  const queryParameters = Object.keys(params)
    .filter(p => !!params[p])
    .map(p => `${p}=${params[p]}`)
    .join('&');

  let routeWithParameters = route;
  if (queryParameters.length > 0) {
    routeWithParameters += `?${queryParameters}`;
  }
  if (redirect) {
    window.location.href = routeWithParameters;
    return;
  }
  navigate(routeWithParameters, { state });
};

export const redirectTo = (route: string, params?: QueryParams) => navigateTo(route, params, true);

export const navigateToAppointmentDetails = (id: string) =>
  navigateTo(generatePath(PATH.KEY_DROP_OFF_APPOINTMENTS_DETAILED.path, { appointmentId: id }));

export const navigateToAppointments = () => navigateTo(PATH.KEY_DROP_OFF_APPOINTMENTS.path);

export function navigateToAudits() {
  navigateTo(PATH.AUDITS.path);
}

export function navigateToAuditModels() {
  navigateTo(generatePath(PATH.AUDIT_MODELS.path));
}

export const navigateToCustomerDashboard = () => navigateTo(PATH.CUSTOMERS.path);

export const navigateToCustomerDetails = (id: string) =>
  navigateTo(generatePath(PATH.CUSTOMERS_DETAILED.path, { customerId: id }));

export const navigateToIntervention = (interventionId: string, module: ModuleEnum) =>
  navigateTo(generatePath(MODULES[module].path, { interventionId }));

export const navigateToForgotPasswordCheckMail = () => navigateTo(`${PATH.FORGOT_PASSWORD.path}/check-mail`);

export const navigateToHomePage = () => navigateTo('/');

export const navigateToAuditModelDetails = (auditModelId: string) =>
  navigateTo(generatePath(PATH.AUDIT_MODELS_DETAILED.path, { auditModelId }));

export const navigateToDealerConfig = (dealerId: string, tabKey = 'general') =>
  navigateTo(generatePath(PATH.DEALER_CONFIG_PAGE.path, { dealerId, tabKey }));

export const navigateToManagement = () => navigateTo(PATH.DEALER_MANAGEMENT.path);

export const navigateToGuidelineDetails = (guidelineId: string, state?: unknown, isGroup = false) =>
  navigateTo(
    generatePath((isGroup ? PATH.GROUP_GUIDELINE_DETAILED : PATH.GUIDELINE_DETAILED).path, { guidelineId }),
    {},
    false,
    state
  );

export const navigateToPredefinedMessagesDetails = (predefinedMessageId: string, state?: unknown) =>
  navigate(`/dealer/management/predefined-messages/${predefinedMessageId}`, { replace: true, state });

export const navigateToDealerAdminConfig = () => navigateTo(PATH.DEALERS.path);

export const navigateToQuestionModelDetails = (questionModelId: string, origin?: OriginParam) =>
  navigateTo(generatePath(PATH.QUESTION_MODEL_DETAILED.path, { questionModelId, origin } as GeneratePathParams));

export const navigateToVehicleCheckModelDetails = (vehicleCheckModelId: string, origin?: OriginParam) =>
  navigateTo(generatePath(PATH.VEHICLE_CHECK_MODEL_DETAILED.path, { vehicleCheckModelId, origin } as GeneratePathParams));
